import { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import { API, AuthContext } from '../App';
import { 
  Lightbulb, Shield, AlertTriangle, CheckCircle, Clock, 
  ChevronDown, ChevronUp, Search, Download, FileText,
  User, Calendar, ArrowRight
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';

export default function RecommendationsPage() {
  const { viewingOrg, enabledFrameworks } = useContext(AuthContext);
  const [recommendations, setRecommendations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedFramework, setSelectedFramework] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedControls, setExpandedControls] = useState({});
  const [filterStatus, setFilterStatus] = useState('all');

  const getViewTenantParam = () => {
    return viewingOrg?.id ? `&view_tenant_id=${viewingOrg.id}` : '';
  };

  useEffect(() => {
    fetchRecommendations();
  }, [selectedFramework, viewingOrg]);

  const fetchRecommendations = async () => {
    setLoading(true);
    try {
      const frameworkParam = selectedFramework !== 'all' ? `framework=${selectedFramework}` : '';
      const response = await axios.get(
        `${API}/controls/non-compliant/recommendations?${frameworkParam}${getViewTenantParam()}`
      );
      setRecommendations(response.data.controls || []);
    } catch (error) {
      console.error('Failed to fetch recommendations:', error);
      toast.error('Failed to load recommendations');
    } finally {
      setLoading(false);
    }
  };

  const toggleExpanded = (controlId) => {
    setExpandedControls(prev => ({
      ...prev,
      [controlId]: !prev[controlId]
    }));
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'no':
      case 'non-compliant':
      case 'not implemented':
        return 'bg-red-100 text-red-800 border-red-200';
      case 'partially':
      case 'partial':
        return 'bg-amber-100 text-amber-800 border-amber-200';
      default:
        return 'bg-slate-100 text-slate-800 border-slate-200';
    }
  };

  const getStatusIcon = (status) => {
    switch (status?.toLowerCase()) {
      case 'no':
      case 'non-compliant':
      case 'not implemented':
        return <AlertTriangle className="w-4 h-4" />;
      case 'partially':
      case 'partial':
        return <Clock className="w-4 h-4" />;
      default:
        return <AlertTriangle className="w-4 h-4" />;
    }
  };

  const formatFrameworkName = (framework) => {
    const names = {
      'ISO27001': 'ISO 27001',
      'SOC2': 'SOC 2',
      'Essential8': 'Essential 8',
      'PCIDSS': 'PCI DSS',
      'HIPAA': 'HIPAA',
      'ISO42001': 'ISO 42001'
    };
    return names[framework] || framework;
  };

  const filteredRecommendations = recommendations.filter(rec => {
    const matchesSearch = 
      rec.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      rec.control_id?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      rec.requirement?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesStatus = filterStatus === 'all' || 
      (filterStatus === 'non-compliant' && ['no', 'non-compliant', 'not implemented'].includes(rec.status?.toLowerCase())) ||
      (filterStatus === 'partial' && ['partially', 'partial'].includes(rec.status?.toLowerCase())) ||
      (filterStatus === 'pending' && !rec.status);
    
    return matchesSearch && matchesStatus;
  });

  const groupedByFramework = filteredRecommendations.reduce((acc, rec) => {
    const framework = rec.framework || 'Other';
    if (!acc[framework]) acc[framework] = [];
    acc[framework].push(rec);
    return acc;
  }, {});

  const exportRecommendations = () => {
    const csvContent = [
      ['Framework', 'Control ID', 'Title', 'Status', 'Requirement', 'Recommendation', 'Owner', 'Due Date'].join(','),
      ...filteredRecommendations.map(rec => [
        rec.framework,
        rec.control_id,
        `"${(rec.title || '').replace(/"/g, '""')}"`,
        rec.status || 'Pending',
        `"${(rec.requirement || '').replace(/"/g, '""').replace(/\n/g, ' ')}"`,
        `"${(rec.recommendation || '').replace(/"/g, '""').replace(/\n/g, ' ')}"`,
        rec.owner || '',
        rec.due_date || ''
      ].join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `recommendations_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
    toast.success('Recommendations exported successfully');
  };

  // Get available frameworks from enabledFrameworks
  const availableFrameworks = enabledFrameworks && enabledFrameworks.length > 0 
    ? enabledFrameworks 
    : ['ISO27001', 'SOC2', 'Essential8', 'PCIDSS', 'HIPAA', 'ISO42001'];

  return (
    <div className="space-y-6" data-testid="recommendations-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-heading font-semibold tracking-tight text-slate-900 flex items-center gap-2">
            <Lightbulb className="w-8 h-8 text-amber-500" />
            Compliance Recommendations
          </h1>
          <p className="text-slate-600 mt-1">
            Actionable recommendations for non-compliant controls across all frameworks
          </p>
        </div>
        <Button onClick={exportRecommendations} className="bg-slate-900 hover:bg-slate-800" data-testid="export-btn">
          <Download className="w-4 h-4 mr-2" />
          Export CSV
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="bg-white border-l-4 border-l-red-500">
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-600">Total Non-Compliant</p>
                <p className="text-2xl font-bold text-slate-900">
                  {recommendations.filter(r => ['no', 'non-compliant', 'not implemented'].includes(r.status?.toLowerCase())).length}
                </p>
              </div>
              <AlertTriangle className="w-8 h-8 text-red-500" />
            </div>
          </CardContent>
        </Card>
        <Card className="bg-white border-l-4 border-l-amber-500">
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-600">Partially Compliant</p>
                <p className="text-2xl font-bold text-slate-900">
                  {recommendations.filter(r => ['partially', 'partial'].includes(r.status?.toLowerCase())).length}
                </p>
              </div>
              <Clock className="w-8 h-8 text-amber-500" />
            </div>
          </CardContent>
        </Card>
        <Card className="bg-white border-l-4 border-l-slate-400">
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-600">Pending Review</p>
                <p className="text-2xl font-bold text-slate-900">
                  {recommendations.filter(r => !r.status).length}
                </p>
              </div>
              <FileText className="w-8 h-8 text-slate-400" />
            </div>
          </CardContent>
        </Card>
        <Card className="bg-white border-l-4 border-l-blue-500">
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-600">Total Recommendations</p>
                <p className="text-2xl font-bold text-slate-900">{recommendations.length}</p>
              </div>
              <Lightbulb className="w-8 h-8 text-blue-500" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card className="bg-white">
        <CardContent className="pt-4">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  placeholder="Search by control ID, title, or requirement..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 bg-white border-slate-300"
                  data-testid="search-input"
                />
              </div>
            </div>
            <Select value={selectedFramework} onValueChange={setSelectedFramework}>
              <SelectTrigger className="w-full md:w-48 bg-white border-slate-300" data-testid="framework-filter">
                <SelectValue placeholder="All Frameworks" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Frameworks</SelectItem>
                {availableFrameworks.map(fw => (
                  <SelectItem key={fw} value={fw}>{formatFrameworkName(fw)}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={filterStatus} onValueChange={setFilterStatus}>
              <SelectTrigger className="w-full md:w-48 bg-white border-slate-300" data-testid="status-filter">
                <SelectValue placeholder="All Statuses" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                <SelectItem value="non-compliant">Non-Compliant</SelectItem>
                <SelectItem value="partial">Partially Compliant</SelectItem>
                <SelectItem value="pending">Pending Review</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Recommendations List */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900"></div>
        </div>
      ) : filteredRecommendations.length === 0 ? (
        <Card className="bg-white">
          <CardContent className="py-12 text-center">
            <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-slate-900">No Recommendations Found</h3>
            <p className="text-slate-600 mt-1">
              {selectedFramework !== 'all' 
                ? `All ${formatFrameworkName(selectedFramework)} controls are compliant or no controls match your filters.`
                : 'All controls are compliant or no controls match your filters.'
              }
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-6">
          {Object.entries(groupedByFramework).map(([framework, controls]) => (
            <div key={framework} className="space-y-3">
              <div className="flex items-center gap-2">
                <Shield className="w-5 h-5 text-slate-700" />
                <h2 className="text-lg font-semibold text-slate-900">
                  {formatFrameworkName(framework)}
                </h2>
                <span className="text-sm text-slate-500">({controls.length} recommendations)</span>
              </div>
              
              <div className="space-y-3">
                {controls.map((rec) => (
                  <Card 
                    key={rec.id || rec.control_id} 
                    className="bg-white hover:shadow-md transition-shadow"
                    data-testid={`recommendation-card-${rec.control_id}`}
                  >
                    <CardHeader 
                      className="cursor-pointer py-4"
                      onClick={() => toggleExpanded(rec.id || rec.control_id)}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-1">
                            <span className="text-sm font-mono font-semibold text-slate-700 bg-slate-100 px-2 py-0.5 rounded">
                              {rec.control_id}
                            </span>
                            <span className={`px-2 py-0.5 text-xs font-medium rounded-full border flex items-center gap-1 ${getStatusColor(rec.status)}`}>
                              {getStatusIcon(rec.status)}
                              {rec.status || 'Pending'}
                            </span>
                          </div>
                          <CardTitle className="text-base font-medium text-slate-900">
                            {rec.title}
                          </CardTitle>
                          <CardDescription className="mt-1 line-clamp-2">
                            {rec.requirement}
                          </CardDescription>
                        </div>
                        <div className="flex items-center gap-2 ml-4">
                          {rec.owner && (
                            <span className="text-xs text-slate-500 flex items-center gap-1">
                              <User className="w-3 h-3" />
                              {rec.owner}
                            </span>
                          )}
                          {expandedControls[rec.id || rec.control_id] ? (
                            <ChevronUp className="w-5 h-5 text-slate-400" />
                          ) : (
                            <ChevronDown className="w-5 h-5 text-slate-400" />
                          )}
                        </div>
                      </div>
                    </CardHeader>
                    
                    {expandedControls[rec.id || rec.control_id] && (
                      <CardContent className="pt-0 border-t border-slate-100">
                        <div className="mt-4 space-y-4">
                          {/* Requirement */}
                          <div>
                            <h4 className="text-sm font-semibold text-slate-700 mb-1">Requirement</h4>
                            <p className="text-sm text-slate-600 whitespace-pre-wrap">{rec.requirement}</p>
                          </div>
                          
                          {/* Recommendation */}
                          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                            <div className="flex items-start gap-2">
                              <Lightbulb className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />
                              <div>
                                <h4 className="text-sm font-semibold text-amber-800 mb-2">Recommendation</h4>
                                <p className="text-sm text-amber-900 whitespace-pre-wrap">{rec.recommendation}</p>
                              </div>
                            </div>
                          </div>
                          
                          {/* Metadata */}
                          <div className="flex flex-wrap gap-4 text-sm">
                            {rec.category && (
                              <div className="flex items-center gap-1 text-slate-600">
                                <FileText className="w-4 h-4" />
                                <span>{rec.category}</span>
                              </div>
                            )}
                            {rec.due_date && (
                              <div className="flex items-center gap-1 text-slate-600">
                                <Calendar className="w-4 h-4" />
                                <span>Due: {new Date(rec.due_date).toLocaleDateString()}</span>
                              </div>
                            )}
                            {rec.remediation_status && (
                              <div className="flex items-center gap-1 text-slate-600">
                                <ArrowRight className="w-4 h-4" />
                                <span>Remediation: {rec.remediation_status}</span>
                              </div>
                            )}
                          </div>
                        </div>
                      </CardContent>
                    )}
                  </Card>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
